// ===========================================
// OCR Types for Google Cloud Vision Integration
// ===========================================

/**
 * Bounding box coordinates for extracted text
 * Normalized coordinates (0-1) relative to image dimensions
 */
export interface BoundingBox {
  topLeft: { x: number; y: number };
  topRight: { x: number; y: number };
  bottomRight: { x: number; y: number };
  bottomLeft: { x: number; y: number };
}

/**
 * Single extracted text block from OCR
 */
export interface ExtractedText {
  text: string;
  confidence: number; // 0-1 confidence score
  boundingBox?: BoundingBox;
}

/**
 * Request payload for OCR text extraction
 */
export interface OCRExtractRequest {
  imageBase64: string; // Base64 encoded JPEG image
  expectedBarcodes?: string[]; // Optional: barcodes to match against
}

/**
 * Response from OCR extraction endpoint
 */
export interface OCRExtractResponse {
  statusCode: string;
  texts: ExtractedText[];
  fullText: string; // Combined text from all blocks
  processingTimeMs: number;
}

/**
 * Validated match between OCR text and expected barcode
 */
export interface OCRMatch {
  barcode: string; // The matched barcode from expected list
  matchedText: string; // The OCR text that matched
  confidence: number; // OCR confidence score
  editDistance: number; // Levenshtein distance (0 = exact match)
  isExactMatch: boolean;
}

/**
 * Result from OCR capture and matching process
 */
export interface OCRResult {
  success: boolean;
  matches: OCRMatch[];
  allExtractedTexts: ExtractedText[];
  fullText: string;
  imageBase64?: string; // For queuing if offline
  timestamp: number;
  error?: string;
}

// ===========================================
// Offline Queue Types for OCR
// ===========================================

/**
 * OCR request queued for offline processing
 */
export interface OCRQueuePayload {
  imageBase64: string;
  expectedBarcodes: string[];
  capturedAt: number;
  boxContext?: {
    orderId?: number;
    workflowType?: string;
  };
}

/**
 * Configuration for OCR capture
 */
export interface OCRCaptureConfig {
  maxImageSizeKB: number; // Maximum compressed image size
  jpegQuality: number; // 0-1 JPEG quality
  minConfidence: number; // Minimum confidence to consider a match
  maxQueuedRequests: number; // Maximum pending OCR requests in queue
  queueExpiryHours: number; // Auto-cleanup after this many hours
}

/**
 * Default OCR capture configuration
 */
export const DEFAULT_OCR_CONFIG: OCRCaptureConfig = {
  maxImageSizeKB: 500,
  jpegQuality: 0.8,
  minConfidence: 0.6,
  maxQueuedRequests: 5,
  queueExpiryHours: 24,
};

// ===========================================
// OCR State Types for UI
// ===========================================

export type OCRProcessingState =
  | 'idle'
  | 'capturing'
  | 'processing'
  | 'success'
  | 'error'
  | 'queued';

export interface OCRUIState {
  processingState: OCRProcessingState;
  lastResult: OCRResult | null;
  error: string | null;
}

// ===========================================
// Batch Sync Types for OCR
// ===========================================

export interface OCRBatchSyncRequest {
  requests: OCRQueuePayload[];
}

export interface OCRBatchSyncResponse {
  statusCode: string;
  processed: number;
  results: Array<{
    payload: OCRQueuePayload;
    result: OCRExtractResponse | null;
    error?: string;
  }>;
}
